import { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import theme from "./theme/theme";
import LoginScreen from "./screen/loginScreen";
import RegisterScreen from "./screen/registerScreen";
import ForgotPasswordScreen from "./screen/forgotPasswordScreen";
import ResetPasswordScreen from "./screen/resetPasswordScreen";
import ChatScreen from "./screen/chatScreen";
import ClientTableScreen from "./screen/clientTableScreen";
import DashboardScreen from "./screen/dashboardScreen";
import ClientDashboardScreen from "./screen/clientDashboardScreen";
import InternalArchiveScreen from "./screen/internalArchiveScreen";
import LogsScreen from "./screen/logsScreen";
import DailyReportScreen from "./screen/dailyReportScreen";
import FormsAndTemplatesScreen from "./screen/formsAndTemplatesScreen";
import auth from "./services/auth";

function getAuthenticatedUser() {
  const user = auth.getCurrentUser();
  return user || null;
}

function getHomeRouteByType(userType) {
  return userType === "client" ? "/clientDashboard" : "/dashboard";
}

function RequireAuth({ children }) {
  const location = useLocation();
  const user = getAuthenticatedUser();

  if (!user) {
    return <Navigate to="/" replace state={{ from: location.pathname }} />;
  }

  return children;
}

function PublicOnly({ children }) {
  const user = getAuthenticatedUser();

  if (user?.data?.type) {
    return <Navigate to={getHomeRouteByType(user.data.type)} replace />;
  }

  return children;
}

function App() {
  const user = getAuthenticatedUser();

  useEffect(() => {
    if (!user?.data?.id || !user?.data?.type || !user?.data?.sessionToken) {
      return;
    }

    const apiBase = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
    const logoutUrl = `${apiBase}/logout.php`;
    const payload = JSON.stringify({
      userId: user.data.id,
      userType: user.data.type,
      sessionToken: user.data.sessionToken,
    });

    const heartbeatInterval = window.setInterval(() => {
      auth.refreshSession();
    }, 60 * 1000);

    // Initial heartbeat on mount.
    auth.refreshSession();

    const sendCloseSignal = () => {
      try {
        let sent = false;

        if (navigator.sendBeacon) {
          const blob = new Blob([payload], { type: "application/json" });
          sent = navigator.sendBeacon(logoutUrl, blob);
        }

        if (!sent) {
          fetch(logoutUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: payload,
            keepalive: true,
          }).catch(() => null);
        }
      } catch (err) {
        // Avoid blocking close flow if beacon/fetch fails.
      }
    };

    const handlePageHide = (event) => {
      // Do not close server session when browser stores this page in BFCache.
      if (event?.persisted) return;
      sendCloseSignal();
    };

    const handleBeforeUnload = () => {
      sendCloseSignal();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        sendCloseSignal();
      }
    };

    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(heartbeatInterval);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [user?.data?.id, user?.data?.type, user?.data?.sessionToken]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Routes>
        <Route
          path="/"
          element={
            <PublicOnly>
              <LoginScreen />
            </PublicOnly>
          }
        />
        <Route
          path="/register"
          element={
            <PublicOnly>
              <RegisterScreen />
            </PublicOnly>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicOnly>
              <ForgotPasswordScreen />
            </PublicOnly>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicOnly>
              <ResetPasswordScreen />
            </PublicOnly>
          }
        />
        <Route
          path="/chat"
          element={
            <RequireAuth>
              <ChatScreen />
            </RequireAuth>
          }
        />
        <Route
          path="/clientTable"
          element={
            <RequireAuth>
              <ClientTableScreen />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard"
          element={
            <RequireAuth>
              <DashboardScreen />
            </RequireAuth>
          }
        />
        <Route
          path="/logs"
          element={
            <RequireAuth>
              <LogsScreen />
            </RequireAuth>
          }
        />
        <Route
          path="/internalArchive"
          element={
            <RequireAuth>
              <InternalArchiveScreen />
            </RequireAuth>
          }
        />
        <Route
          path="/dailyReports"
          element={
            <RequireAuth>
              <DailyReportScreen />
            </RequireAuth>
          }
        />
        <Route
          path="/clientDashboard"
          element={
            <RequireAuth>
              <ClientDashboardScreen />
            </RequireAuth>
          }
        />
        <Route
          path="/formsAndTemplates"
          element={
            <RequireAuth>
              <FormsAndTemplatesScreen />
            </RequireAuth>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ThemeProvider>
  );
}

export default App;
